import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5498ESAInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 5498-ESA instructions for Tax Year 2025"
          description="Learn IRS Form 5498-ESA step-by-step instructions for Tax Year 2025. TaxZerone helps you accurately report contributions to Education Savings Accounts (ESA)."
          keywords=""
          canonical="resources/information-returns/form-5498-esa-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 5498-ESA</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Follow our step-by-step instructions to file accurately with IRS
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498ESAinstruction`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-5498-esa-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 5498-ESA at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-ESA
                        </strong>
                        {' reports contributions made to a '}
                        <strong style={{ fontWeight: 600 }}>
                          Coverdell Education Savings Account (ESA)
                        </strong>
                        . Trustees or issuers use this form to report total
                        contributions made to a beneficiary's ESA during the tax
                        year.
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>April 30</strong>
                        {
                          ' following the calendar year being reported. The IRS filing deadline is '
                        }
                        <strong style={{ fontWeight: 600 }}>May 31</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions,
                        </strong>
                        {
                          ' including trustee or issuer information and beneficiary details, '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          Box 1 (Coverdell ESA Contributions)
                        </strong>
                        {' and '}
                        <strong style={{ fontWeight: 600 }}>
                          Box 2 (Rollover Contributions)
                        </strong>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-ESA
                        </strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Form 5498-ESA Coverdell ESA Contribution Information is an IRS
                  form used to report contributions to a Coverdell Education
                  Savings Account (ESA). This resource page provides an
                  overview, filing requirements, deadlines, and step-by-step
                  instructions for completing and submitting{' '}
                  <span style={{ fontWeight: 600 }}>Form 5498-ESA</span>.
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 5498-ESA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 5498-ESA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#import" sx={{ color: '#0202ff' }}>
                          Important dates for filing Form 5498-ESA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#info" sx={{ color: '#0202ff' }}>
                          Information required to file Form 5498-ESA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          Instructions to file Form 5498-ESA
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#simple"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Simplify your Form 5498-ESA filing with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 5498-ESA?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {/* <Link
                    href="/information-returns/efile-irs-form-5498-esa/"
                    sx={{ color: '#0202ff' }}
                    
                  > */}{' '}
                  Form 5498-ESA
                  {/* </Link>{' '} */} is used by trustees or custodians to
                  report contributions-including rollover contributions to any
                  Coverdell ESA for a beneficiary during the tax year. The form
                  provides the IRS and the beneficiary with information about
                  annual contributions, rollovers, and the account’s identifying
                  details{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 3, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Simplify your 5498-ESA filing for Coverdell ESA
                        contributions and fair market values.
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 5498-ESA with TaxZerone for fast, accurate
                        education savings account reporting today.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498ESAinstructions`}
                        id="who"
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 5498-ESA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Trustees or issuers must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-esa/"
                    sx={{ color: '#0202ff' }}
                    id="import"
                  >
                    file 5498-ESA Form
                  </Link>{' '}
                  to report Coverdell Education Savings Account (ESA) such as a
                  bank or financial institution that holds the account. This
                  form has two copies:{' '}
                  <span style={{ fontWeight: 600 }}>Copy A</span> must be
                  submitted to the IRS and{' '}
                  <span style={{ fontWeight: 600 }}>Copy B</span> should be
                  issued to the beneficiary.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates for filing 5498-ESA
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Staying on top of{' '}
                  <Link
                    href="/resources/information-returns/form-5498-esa-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 5498-esa deadlines
                  </Link>{' '}
                  is essential for accurate and timely reporting of Coverdell
                  ESA contributions.{' '}
                </Typography>

                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 5498-ESA
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Send Beneficiary copy </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            April 30, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>IRS E-filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Paper filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#bbdefb',
                    border: '1px solid #ccc',
                  }}
                  // className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { md: 18, sm: 16, xs: 14 },
                        }}
                      >
                        Need More Time to File Form 5498-ESA?
                      </Typography>
                      <Typography variant="body1">
                        No worries! Get a{' '}
                        <span style={{ fontWeight: 600 }}>
                          30-day extension
                        </span>{' '}
                        with Form 8809 to avoid penalties and file
                        stress-free.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#213e99',
                          color: '#fff',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498ESAinstructions`}
                        id="info"
                      >
                        E-file Form 8809
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Information Required to file Form 5498-ESA
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Trustee’s name, address, and TIN </li>
                  <li>
                    Beneficiary’s legal name, address, account number, and
                    TIN{' '}
                  </li>
                  <li>
                    Total contributions made for the tax year (including those
                    made by the April 15 deadline for the prior year){' '}
                  </li>
                  <li id="file">Rollover contributions, if any </li>
                  <li>
                    Account number (if multiple accounts for one
                    beneficiary){' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Instructions to file Form 5498-ESA
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Read the step-by-step instructions to ensure a smooth filing
                  with the IRS.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f5498-esa-trustee-img.webp" />
                  <source srcSet="/assets/images/f5498-esa-trustee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5498-esa-trustee-img.png"
                    loading="lazy"
                    alt=" Instructions to file Form 5498-ESA"
                    width={410}
                    height={122}
                    className="img-responsive"
                    sx={{
                      // border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the Trustee’s or Issuer’s legal name, address including
                  City, State, country and ZIP code.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f5498-esa-legal-img.webp" />
                  <source srcSet="/assets/images/f5498-esa-legal-img.png" />
                  <img
                    src="/assets/images/f5498-esa-legal-img.png"
                    loading="lazy"
                    alt="Enter the trustee’s or Issuer’s"
                    width={410}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the trustee’s or Issuer’s and Beneficiary’s TIN
                  (Taxpayer Identification Number) in the appropriate field.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-esa-beneficiary-img.webp" />
                  <source srcSet="/assets/images/f5498-esa-beneficiary-img.png" />
                  <img
                    src="/assets/images/f5498-esa-beneficiary-img.png"
                    loading="lazy"
                    alt="Enter the Beneficiary's legal name"
                    width={410}
                    height={182}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the Beneficiary&apos;s legal name, address including
                  apartment number, city, state, country, ZIP code and account
                  number the trustee/issuer given to distinguish the account.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Line 1: Coverdell ESA Contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-esa-coverdell-img.webp" />
                  <source srcSet="/assets/images/f5498-esa-coverdell-img.png" />
                  <img
                    src="/assets/images/f5498-esa-coverdell-img.png"
                    loading="lazy"
                    alt="Line 1: Coverdell ESA Contributions "
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Coverdell Education Savings Account (ESA)
                  contributions made by beneficiary for the year 2025 and
                  through April 15, 2026. For the tax year 2025, total
                  contributions made to the beneficiary’s Coverdell Education
                  Savings Account exceeded $2,000, the excess amount along with
                  earnings must withdraw by June 01, 2026 to avoid penalties.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Line 2: Rollover contributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-esa-rollover-img.webp" />
                  <source srcSet="/assets/images/f5498-esa-rollover-img.png" />
                  <img
                    src="/assets/images/f5498-esa-rollover-img.png"
                    loading="lazy"
                    alt="Line 2: Rollover contributions"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }} id="simple">
                  Enter the rollover contributions made by beneficiary in the
                  year 2025 including direct rollovers and contributions of a
                  military death gratuity. The amount which is rolled over from
                  one to another Coverdell ESA of the same beneficiary or a
                  family member under age 30 (excluding beneficiaries with
                  special needs) are not taxable.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Simplify your Form 5498-ESA filing with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  With TaxZerone, you can easily{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-esa/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 5498-ESA
                  </Link>{' '}
                  in just 3 steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 1:</span> Log in to
                  your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498ESAinstructions`}
                    sx={{ color: '#0202ff' }}
                  >
                    TaxZerone Free Account
                  </Link>{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 2:</span> Choose{' '}
                  <span style={{ fontWeight: 600 }}>“Form 5498-ESA”</span> and
                  enter the details of Trustee, participant and IRA information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 3:</span> Review and
                  transmit it to the IRS and send participant copies through
                  ZeroneVault or postal mail.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the IRS
                        <Link
                          href="https://www.irs.gov/pub/irs-prior/i5498e--2024.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions for form 5498-ESA
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General Instructions for Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              TaxZerone makes your IRS Form filing simple and quick
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Filing starts at{' '}
              <span style={{ fontWeight: 600 }}>$ 2.49 per</span> form and can
              go as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 Per form</span>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498ESAinstructions`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5498ESAInstructions;
